import { computed } from 'vue';
import type { WizardStep } from '../components/WizardSidebar/WizardSidebar.types';
import {
  type InitializeStepperOptions,
  useWizardStore,
} from '../store/wizard-stepper.store';

export function useWizard(namespace: string) {
  const stepperStore = useWizardStore();

  const initializeSteps = (
    steps: WizardStep[],
    options?: InitializeStepperOptions,
  ) => {
    stepperStore.initializeStepper(namespace, steps, options);
  };

  const nextStep = () => {
    stepperStore.nextStep(namespace);
  };

  const prevStep = () => {
    stepperStore.previousStep(namespace);
  };

  const goToStep = (stepName: string) => {
    stepperStore.goToStep(namespace, stepName);
  };

  const completeWizard = () => {
    stepperStore.completeStepper(namespace);
  };

  const isCompleted = computed(
    () => stepperStore.steppers[namespace]?.completed ?? false,
  );

  const setProps = (props: Record<string, unknown>) => {
    stepperStore.setStepperProps(namespace, props);
  };

  const getProps = computed(() => {
    return stepperStore.steppers[namespace]?.sharedProps;
  });

  const activeIndex = computed(
    () => stepperStore.steppers[namespace]?.activeIndex,
  );

  const maxReachedIndex = computed(
    () => stepperStore.steppers[namespace]?.maxReachedIndex,
  );

  const totalSteps = computed(
    () => stepperStore.steppers[namespace]?.steps.length ?? 0,
  );

  const currentActiveComponent = computed(() => {
    const index = activeIndex.value;
    if (index === undefined) {
      return undefined;
    }
    return stepperStore.steppers[namespace]?.steps[index]?.component;
  });

  const canGoBack = computed<boolean>(() => (activeIndex.value ?? 0) > 0);

  const canGoNext = computed<boolean>(
    () => (activeIndex.value ?? 0) < totalSteps.value - 1,
  );

  const getStepIndexByName = (stepName: string) => {
    return stepperStore.steppers[namespace]?.steps.findIndex(
      (step) => step.name === stepName,
    );
  };

  const isActiveStep = (stepName: string) => {
    const stepper = stepperStore.steppers[namespace];
    if (!stepper) {
      return false;
    }
    return stepper.activeIndex === getStepIndexByName(stepName);
  };

  const isStepReached = (stepName: string) => {
    const stepper = stepperStore.steppers[namespace];
    const index = getStepIndexByName(stepName);
    if (!stepper || index === undefined || index < 0) {
      return false;
    }
    return index <= stepper.maxReachedIndex;
  };

  const isRecapStep = (stepName: string) => {
    const stepper = stepperStore.steppers[namespace];
    const index = getStepIndexByName(stepName);
    if (!stepper || index === undefined || index < 0) {
      return false;
    }
    return stepper.steps[index].isRecap ?? false;
  };

  const destroyStepper = () => {
    stepperStore.destroyStepper(namespace);
  };

  return {
    initializeSteps,
    nextStep,
    prevStep,
    goToStep,
    activeIndex,
    maxReachedIndex,
    currentActiveComponent,
    canGoBack,
    canGoNext,
    isActiveStep,
    isStepReached,
    totalSteps,
    completeWizard,
    isCompleted,
    destroyStepper,
    setProps,
    getProps,
    isRecapStep,
  };
}
